import { FC, MouseEvent, ReactElement } from 'react'
import { useNavigate } from 'react-router'
import classnames from 'classnames'
import type {
    ChannelAvatarProps,
    ChannelPreviewUIComponentProps,
} from 'stream-chat-react'
import { ChannelPreviewMessenger, useChatContext } from 'stream-chat-react'

import { Avatar } from '@/components'
import { formatConversationTimestamp } from '@/helpers/date'
import { getDirectMessageTitle } from '@/helpers/directMessageTitle'
import { trans } from '@/helpers/trans'

import './ConversationListItem.scss'

export const ConversationListItem: FC<ChannelPreviewUIComponentProps> = ({
    channel,
    className = '',
    displayTitle,
    lastMessage,
    onSelect,
    setActiveChannel,
    ...previewProps
}): ReactElement => {
    const { client } = useChatContext('ConversationListItem')
    const navigate = useNavigate()
    const itemClassName = classnames('conversation-list-item', className)
    const isGroup = channel.type === 'team'
    const ConversationAvatar: FC<ChannelAvatarProps> = ({ image, name }): ReactElement => (
        <Avatar
            icon={isGroup ? 'group' : undefined}
            image={image ?? undefined}
            initials={isGroup ? undefined : name?.charAt(0)}
            name={name}
        />
    )
    const openConversation = (event: MouseEvent): void => {
        const path = [
            trans('nav.communication.link'),
            trans('nav.communication.messages.link'),
            channel.id,
        ].join('/')

        if (onSelect) {
            onSelect(event)
        } else {
            setActiveChannel?.(channel)
        }

        navigate(`/${path}`)
    }

    return (
        <div className={itemClassName}>
            <ChannelPreviewMessenger
                {...previewProps}
                Avatar={ConversationAvatar}
                channel={channel}
                className="conversation-list-item__preview"
                displayTitle={getDirectMessageTitle(channel, client.userID) ?? displayTitle}
                onSelect={openConversation}
                setActiveChannel={setActiveChannel}
            />
            {lastMessage && (
                <time className="conversation-list-item__time" dateTime={lastMessage.created_at.toISOString()}>
                    {formatConversationTimestamp(lastMessage.created_at)}
                </time>
            )}
        </div>
    )
}
